import { Eye, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { adminRepository } from "@/repositories/adminRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * Says, on every screen, that this is SmartSpaza support looking at somebody's
 * shop — whose, until when, and that nothing can be changed — with the way out.
 *
 * Leaves on its own when the hour is up. The server has already stopped
 * honouring the view by then; leaving here just stops the screens pretending.
 */
export function SupportBanner() {
  const session = useAuthStore((s) => s.session);
  const exitSupportView = useAuthStore((s) => s.exitSupportView);
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  const merchantId = session?.activeMerchantId ?? null;
  const supportView = session?.supportView ?? null;

  const leave = async () => {
    if (!merchantId || leaving) return;
    setLeaving(true);
    try {
      await adminRepository.endSupportView(merchantId);
    } catch {
      // Already ended or expired on the server. Leaving locally is what matters.
    }
    await exitSupportView();
    void navigate(`/admin/merchants/${merchantId}`, { replace: true });
  };

  useEffect(() => {
    if (!supportView) return;
    const remaining = new Date(supportView.expiresAt).getTime() - Date.now();
    const timer = window.setTimeout(() => void leave(), Math.max(remaining, 0));
    return () => window.clearTimeout(timer);
  }, [supportView?.expiresAt]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!supportView || !session?.merchant) return null;

  const ends = new Date(supportView.expiresAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-gold-500 px-4 py-2 text-sm text-navy-800"
    >
      <Eye className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        <strong>Viewing {session.merchant.name} as SmartSpaza support.</strong>{" "}
        Read-only — nothing can be changed. Ends at {ends}.
      </p>
      <button
        type="button"
        onClick={() => void leave()}
        disabled={leaving}
        className="inline-flex h-9 items-center gap-1.5 rounded-control bg-navy-800 px-3 font-semibold text-white hover:bg-navy-700 disabled:opacity-60"
      >
        <LogOut className="size-4" aria-hidden /> Exit support view
      </button>
    </div>
  );
}
